import { openDB } from 'idb';

const DB_NAME = 'pos-products-db';
const DB_VERSION = 1;
const STORE_NAME = 'products';

// Инициализация базы данных
const initDB = async () => {
    try {
        return await openDB(DB_NAME, DB_VERSION, {
            upgrade(db) {
                // Создаем хранилище products если его еще нет
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    const store = db.createObjectStore(STORE_NAME, {
                        keyPath: 'id'
                    });

                    // Создаем индексы для быстрого поиска
                    store.createIndex('barcode', 'barcodes', { multiEntry: true });
                    store.createIndex('name', 'name');
                    store.createIndex('external_id', 'external_id');
                }
            },
        });
    } catch (error) {
        console.error('Ошибка инициализации базы данных:', error);
        throw error;
    }
};

// Сохранение всех товаров в IndexedDB
export const saveProductsToIndexedDB = async (products) => {
    try {
        const db = await initDB();
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);

        // Очищаем хранилище перед добавлением новых данных
        await store.clear();

        // Добавляем все товары за одну транзакцию
        const promises = products.map(product => store.put(product));
        await Promise.all(promises);

        await tx.complete;
        console.log(`Сохранено ${products.length} товаров в IndexedDB`);

        return true;
    } catch (error) {
        console.error('Ошибка сохранения товаров в IndexedDB:', error);
        throw error;
    }
};

// Поиск товара по штрихкоду
export const getProductByBarcode = async (barcode) => {
    try {
        if (!barcode) return null;

        const db = await initDB();
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const index = store.index('barcode');

        const product = await index.get(barcode);
        await tx.complete;

        return product || null;
    } catch (error) {
        console.error('Ошибка поиска товара по штрихкоду:', error);
        return null;
    }
};

// Поиск товара по ID (точное совпадение)
export const getProductById = async (id) => {
    try {
        if (!id) return null;

        const db = await initDB();
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);

        const product = await store.get(Number(id));
        await tx.complete;

        return product || null;
    } catch (error) {
        console.error('Ошибка поиска товара по ID:', error);
        return null;
    }
};

// Поиск товаров по названию (частичный поиск)
export const getProductsByName = async (searchTerm, limit = 50) => {
    try {
        if (!searchTerm || searchTerm.length < 2) return [];

        const db = await initDB();
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);

        const products = [];
        const searchTermLower = searchTerm.toLowerCase();

        // Используем курсор для поиска по названию
        let cursor = await store.openCursor();

        while (cursor && products.length < limit) {
            const product = cursor.value;

            if (product.name && product.name.toLowerCase().includes(searchTermLower)) {
                products.push(product);
            }

            cursor = await cursor.continue();
        }

        await tx.complete;

        return products;
    } catch (error) {
        console.error('Ошибка поиска товаров по названию:', error);
        return [];
    }
};

// Получение всех товаров (с пагинацией)
export const getAllProducts = async (offset = 0, limit = 100) => {
    try {
        const db = await initDB();
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);

        const products = [];
        let cursor = await store.openCursor();
        let currentOffset = 0;

        while (cursor && products.length < limit) {
            if (currentOffset >= offset) {
                products.push(cursor.value);
            }
            currentOffset++;
            cursor = await cursor.continue();
        }

        await tx.complete;

        return products;
    } catch (error) {
        console.error('Ошибка получения всех товаров:', error);
        return [];
    }
};

// Получение количества товаров в базе
export const getProductsCount = async () => {
    try {
        const db = await initDB();
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);

        const count = await store.count();
        await tx.complete;

        return count;
    } catch (error) {
        console.error('Ошибка получения количества товаров:', error);
        return 0;
    }
};

// Очистка хранилища товаров
export const clearProducts = async () => {
    try {
        const db = await initDB();
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);

        await store.clear();
        await tx.complete;

        // Также очищаем флаг синхронизации
        localStorage.removeItem('products_last_sync');

        console.log('Хранилище товаров очищено');
        return true;
    } catch (error) {
        console.error('Ошибка очистки хранилища товаров:', error);
        throw error;
    }
};

// Проверка, нужна ли синхронизация (менее 1 часа назад)
export const needsSync = () => {
    const lastSync = localStorage.getItem('products_last_sync');

    if (!lastSync) {
        return true;
    }

    const lastSyncTime = new Date(lastSync);
    const now = new Date();
    const hourInMs = 60 * 60 * 1000; // 1 час в миллисекундах

    return (now - lastSyncTime) > hourInMs;
};

// Установка времени последней синхронизации
export const setLastSyncTime = () => {
    localStorage.setItem('products_last_sync', new Date().toISOString());
};

// Получение времени последней синхронизации
export const getLastSyncTime = () => {
    const lastSync = localStorage.getItem('products_last_sync');
    return lastSync ? new Date(lastSync) : null;
};

// Проверка доступности IndexedDB
export const isIndexedDBAvailable = () => {
    try {
        return 'indexedDB' in window && window.indexedDB !== null;
    } catch (error) {
        return false;
    }
};